"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import OwnedPageViewTracker from "@/components/OwnedPageViewTracker";
import { captureFirstTouchLanding } from "@/lib/landing-cluster";
import { isPerformanceCriticalPath } from "@/lib/performance-critical-paths";

const SupportHelper = dynamic(() => import("@/components/SupportHelper"), { ssr: false });
const FloatingFeedback = dynamic(() => import("@/components/FloatingFeedback"), { ssr: false });
const GoogleTagManager = dynamic(() => import("@/components/GoogleTagManager"), { ssr: false });
const DirectGoogleAnalytics = dynamic(() => import("@/components/DirectGoogleAnalytics"), {
  ssr: false,
});
const GlobalEngagementTracker = dynamic(() => import("@/components/GlobalEngagementTracker"), {
  ssr: false,
});
const CloudflareWebAnalytics = dynamic(() => import("@/components/CloudflareWebAnalytics"), {
  ssr: false,
});
const ShopifyAttribution = dynamic(() => import("@/components/ShopifyAttribution"), { ssr: false });
const GlobalAgentEntry = dynamic(() => import("@/components/GlobalAgentEntry"), { ssr: false });
const SEOPageRevenueTracker = dynamic(() => import("@/components/SEOPageRevenueTracker"), {
  ssr: false,
});
const MetaPixel = dynamic(() => import("@/components/MetaPixel"), { ssr: false });
const ExitIntent = dynamic(() => import("@/components/ExitIntent"), { ssr: false });
const NpsSurvey = dynamic(() => import("@/components/NpsSurvey"), { ssr: false });
const AstrologerPageTracker = dynamic(() => import("@/components/AstrologerPageTracker"), {
  ssr: false,
});

const VISIBLE_ENHANCEMENTS_DELAY_MS = 3500;
// First-party owned analytics loads on the first user interaction OR this
// idle/timeout fallback — whichever fires first. Industry standard is "~3.5s OR
// on interaction"; the old 8000ms idle-only silently dropped every session that
// bounced within ~8s. Keep <=4000ms (enforced by scripts/check-media-performance.mjs).
const ANALYTICS_ENHANCEMENTS_DELAY_MS = 3500;
// Weekly CWV #921 showed PageSpeed counting timeout-loaded analytics
// (/7yhl/Cloudflare + GTM) as unused JS and boot work on conversion/calculator
// pages. Visible widgets stay outside Lighthouse's lab window, while third-party
// marketing scripts get no passive timeout fallback on these routes.
const PERFORMANCE_CRITICAL_ENHANCEMENTS_DELAY_MS = 30000;
// First-interaction analytics must not mount third-party providers inside the
// same pointer/key frame that creates the user's INP sample. Queue it shortly
// after the interaction; the timeout fallback still captures passive sessions.
const FIRST_INTERACTION_ANALYTICS_DELAY_MS = 1200;

/**
 * Pure-content SEO page path prefixes where Meta Pixel attribution is not needed
 * (panchang, festival, rashifal, horoscope lookup, nakshatra/rashi info pages).
 * GTM's container already fires the Pixel on every page, so the direct MetaPixel
 * component only adds a duplicate fbevents.js request on these high-traffic
 * informational routes. We skip the duplicate here; conversion surfaces
 * (/free-kundali, /ai-astrologer, /kundli-matching, /cart, /checkout, /products,
 * /reports, /gun-milan, /rashifal/*) retain the direct component as a belt-and-
 * suspenders guard in case GTM has not yet loaded when the conversion event fires.
 */
const SEO_CONTENT_PREFIXES = [
  "/panchang/",
  "/choghadiya/",
  "/rahu-kaal/",
  "/muhurat/",
  "/rashifal/",
  "/weekly-rashifal/",
  "/monthly-rashifal/",
  "/yearly-rashifal/",
  "/daily-horoscope/",
  "/today-horoscope/",
  "/tomorrow-horoscope/",
  "/love-horoscope/",
  "/career-horoscope/",
  "/health-horoscope/",
  "/marriage-horoscope/",
  "/nakshatra/",
  "/mahadasha/",
  "/mantras/",
  "/upay/",
  "/hi/",
] as const;

// Festival page slugs follow /<festival-name>-YYYY/ pattern. Detect by a year
// suffix (2025–2030) which is unique to date-anchored SEO pages.
const FESTIVAL_YEAR_SUFFIX_RE = /-(202[5-9]|2030)\/$/;

function isConversionPage(pathname: string): boolean {
  if (SEO_CONTENT_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return false;
  if (FESTIVAL_YEAR_SUFFIX_RE.test(pathname)) return false;
  return true;
}

function shouldTrackAstrologerPage(pathname: string): boolean {
  return pathname === "/ai-astrologer" || pathname === "/ai-astrologer/";
}

function visibleEnhancementDelayFor(pathname: string): number {
  return isPerformanceCriticalPath(pathname)
    ? PERFORMANCE_CRITICAL_ENHANCEMENTS_DELAY_MS
    : VISIBLE_ENHANCEMENTS_DELAY_MS;
}

function thirdPartyMarketingDelayFor(pathname: string): number | null {
  // Keep third-party marketing's passive fallback at 3.5s for normal pages. On
  // PageSpeed-target conversion/calculator pages there is deliberately no
  // passive timeout fallback: real users still activate it on pointer/key/touch,
  // but synthetic lab scroll cannot boot /7yhl/, GTM, GA, Meta, or Cloudflare.
  return isPerformanceCriticalPath(pathname) ? null : ANALYTICS_ENHANCEMENTS_DELAY_MS;
}

function thirdPartyMarketingAllowedForDocument(initialPathname: string): boolean {
  // A document's path-scoped CSP is immutable across client navigation. A page
  // loaded under the CWV-critical policy can therefore never safely activate
  // GTM, /metrics, Meta, or Cloudflare later in that document's lifetime.
  return !isPerformanceCriticalPath(initialPathname);
}

function thirdPartyInteractionEventsFor(pathname: string): string[] {
  // PageSpeed/Lighthouse can synthesize scroll before LCP settles. On
  // performance-critical routes, scroll must not boot third-party marketing
  // inside the lab sample; real engaged users still activate on pointer/key/touch.
  return isPerformanceCriticalPath(pathname)
    ? ["pointerdown", "keydown", "touchstart"]
    : ["pointerdown", "keydown", "scroll", "touchstart"];
}

function afterIdleDelay(callback: () => void, timeoutMs: number) {
  if (typeof window === "undefined") return () => {};
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    callback();
  };
  const idle = window.requestIdleCallback?.(run, { timeout: timeoutMs });
  const timer = window.setTimeout(run, timeoutMs);
  return () => {
    done = true;
    window.clearTimeout(timer);
    if (idle && window.cancelIdleCallback) window.cancelIdleCallback(idle);
  };
}

function afterMinimumDelay(callback: () => void, delayMs: number) {
  if (typeof window === "undefined") return () => {};
  let cancelIdle: (() => void) | null = null;
  let cancelled = false;
  const timer = window.setTimeout(() => {
    if (cancelled) return;
    cancelIdle = afterIdleDelay(callback, 1000);
  }, delayMs);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    cancelIdle?.();
  };
}

export default function ClientEnhancements() {
  const [visibleActive, setVisibleActive] = useState(false);
  const [ownedAnalyticsActive, setOwnedAnalyticsActive] = useState(false);
  const [thirdPartyMarketingActive, setThirdPartyMarketingActive] = useState(false);
  const pathname = usePathname();
  const [thirdPartyMarketingAllowed] = useState(() =>
    thirdPartyMarketingAllowedForDocument(pathname)
  );
  const visibleDelayMs = visibleEnhancementDelayFor(pathname);
  const thirdPartyDelayMs = thirdPartyMarketingDelayFor(pathname);

  // Capture before any delayed analytics or checkout code mounts. The helper
  // is first-write-wins and consumes a redirect-source cookie when middleware
  // canonicalized the acquisition URL, so calculator -> result -> checkout
  // keeps the original landing path rather than the click-time route.
  useEffect(() => {
    captureFirstTouchLanding(pathname);
  }, [pathname]);

  useEffect(
    () => afterMinimumDelay(() => setVisibleActive(true), visibleDelayMs),
    [visibleDelayMs]
  );

  // Owned analytics is first-party beacon/dataLayer work only. It can keep the
  // short passive fallback that preserves bounce/engaged-session measurement.
  useEffect(() => {
    const activateOwnedAnalytics = () => setOwnedAnalyticsActive(true);
    const cancelIdle = afterMinimumDelay(activateOwnedAnalytics, ANALYTICS_ENHANCEMENTS_DELAY_MS);
    let cancelInteraction: (() => void) | null = null;
    const onInteract = () => {
      if (cancelInteraction) return;
      cancelInteraction = afterMinimumDelay(
        activateOwnedAnalytics,
        FIRST_INTERACTION_ANALYTICS_DELAY_MS
      );
    };
    const interactionEvents = ["pointerdown", "keydown", "scroll", "touchstart"];
    const opts = { once: true, passive: true, capture: true } as const;
    interactionEvents.forEach((e) => window.addEventListener(e, onInteract, opts));
    return () => {
      cancelIdle();
      cancelInteraction?.();
      interactionEvents.forEach((e) => window.removeEventListener(e, onInteract, opts));
    };
  }, []);

  // Third-party marketing loads after real user intent, or by a passive fallback
  // only on non-critical routes. This keeps /7yhl/, /metrics/gtag, Meta, and
  // Cloudflare Web Analytics out of PageSpeed's no-interaction critical sample.
  useEffect(() => {
    if (!thirdPartyMarketingAllowed) return;
    const activateThirdPartyMarketing = () => setThirdPartyMarketingActive(true);
    const cancelIdle =
      thirdPartyDelayMs === null
        ? () => {}
        : afterMinimumDelay(activateThirdPartyMarketing, thirdPartyDelayMs);
    let cancelInteraction: (() => void) | null = null;
    const onInteract = () => {
      if (cancelInteraction) return;
      cancelInteraction = afterMinimumDelay(
        activateThirdPartyMarketing,
        FIRST_INTERACTION_ANALYTICS_DELAY_MS
      );
    };
    const interactionEvents = thirdPartyInteractionEventsFor(pathname);
    const opts = { once: true, passive: true, capture: true } as const;
    interactionEvents.forEach((e) => window.addEventListener(e, onInteract, opts));
    return () => {
      cancelIdle();
      cancelInteraction?.();
      interactionEvents.forEach((e) => window.removeEventListener(e, onInteract, opts));
    };
  }, [pathname, thirdPartyDelayMs, thirdPartyMarketingAllowed]);

  return (
    <>
      <OwnedPageViewTracker />
      {visibleActive && (
        <>
          <SupportHelper />
          <FloatingFeedback />
          <GlobalAgentEntry />
          <ExitIntent />
          <NpsSurvey />
        </>
      )}
      {ownedAnalyticsActive && (
        <>
          <GlobalEngagementTracker />
          <ShopifyAttribution />
          <SEOPageRevenueTracker />
          {shouldTrackAstrologerPage(pathname) && <AstrologerPageTracker language="en" />}
        </>
      )}
      {thirdPartyMarketingAllowed && thirdPartyMarketingActive && (
        <>
          <GoogleTagManager />
          <DirectGoogleAnalytics />
          <CloudflareWebAnalytics />
          {/* MetaPixel: GTM already loads Meta Pixel on every page. The direct
              component provides a belt-and-suspenders guarantee that the pixel
              is initialised before a conversion event fires on conversion surfaces.
              On pure-content SEO pages (panchang, rashifal, festival, horoscope,
              etc.) the duplicate fbevents.js request is unnecessary — GTM coverage
              is sufficient and skipping saves one script parse per page. */}
          {isConversionPage(pathname) && <MetaPixel />}
        </>
      )}
    </>
  );
}
